/**
 * Server-side debug system implementation.
 * Extends ServerSystem(BaseDebugSystem) so that TypeScript sees all IServerSystem
 * members, while ServerProcess.onAddedSystem()/bindServerMethods() injects the
 * actual dispatch methods at runtime.
 */

import {
  BaseDebugSystem,
  type DebugLogEntry,
  type SystemDebugInfo,
  type EntityDebugInfo,
  type SystemDebugUpdateOptions,
  type EntityDebugUpdateOptions,
  type SystemMessage,
  DEBUG_MESSAGES,
} from '@gotti/core';
import { ServerSystem } from '../IServerSystem.js';

/**
 * Server debug system configuration options.
 */
export type ServerDebugSystemConfig = {
  /** Whether debug is enabled by default */
  enabled?: boolean;
  /** Maximum logs per system/entity */
  maxLogs?: number;
  /** Whether to auto-sync debug info to clients */
  autoSync?: boolean;
  /** Sync interval in ms (default: 1000) */
  syncInterval?: number;
};

/**
 * Area metadata for server debug logging.
 */
export type ServerDebugMeta = {
  areaId?: string;
  areaType?: string;
};

/**
 * Server-side debug system.
 * Provides debug logging and inspection for server systems and entities.
 * Can broadcast debug info to connected clients for display in the debug UI.
 *
 * Dispatch methods (dispatchToAllClients, etc.) are injected at runtime by
 * `ServerProcess.decorate()` + `bindServerMethods()`. The `ServerSystem()` helper
 * tells TypeScript about them without any per-class `declare` boilerplate.
 */
export class ServerDebugSystem extends ServerSystem(BaseDebugSystem) {
  // -- BaseDebugSystem abstract implementation --
  protected readonly source: 'client' | 'server' = 'server';

  // -- Server-specific properties --
  /** Configuration */
  protected readonly config: ServerDebugSystemConfig;

  /** Sync interval handle */
  private syncIntervalId?: ReturnType<typeof setInterval>;

  /** Pending sync data */
  private pendingSyncData: {
    systems: Set<string>;
    entities: Set<string | number>;
    logs: DebugLogEntry[];
  } = { systems: new Set(), entities: new Set(), logs: [] };

  constructor(config: ServerDebugSystemConfig = {}) {
    super();
    this.config = {
      enabled: true,
      maxLogs: 100,
      autoSync: true,
      syncInterval: 1000,
      ...config,
    };
    this._enabled = this.config.enabled!;
    this.maxLogs = this.config.maxLogs!;
  }

  // -- Lifecycle hooks --

  /**
   * Called when the system is initialized.
   */
  onInit(): void {
    super.onInit();

    // Set up auto-sync if enabled
    if (this.config.autoSync && this.config.syncInterval && this.config.syncInterval > 0) {
      this.syncIntervalId = setInterval(() => {
        this.flushSync();
      }, this.config.syncInterval);
    }
  }

  /**
   * Called when the system is stopped.
   */
  onStop(): void {
    if (this.syncIntervalId) {
      clearInterval(this.syncIntervalId);
      this.syncIntervalId = undefined;
    }
  }

  /**
   * Handle debug messages from clients.
   */
  onClientMessage(clientId: string, message: SystemMessage): void {
    if (!this._enabled) return;

    switch (message.type) {
      case DEBUG_MESSAGES.REQUEST_DEBUG_STATE:
        // Send full debug state to requesting client.
        this.sendFullStateToClient(clientId);
        break;
      case DEBUG_MESSAGES.SET_ENABLED:
        // Allow clients to enable/disable debug (for development)
        if (typeof message.data?.enabled === 'boolean') {
          this._enabled = message.data.enabled;
        }
        break;
    }
  }

  // -- Overrides (server adds sync + source tagging) --

  /**
   * Get area metadata for log entries.
   */
  protected override getAreaMeta(): { areaId?: string; areaType?: string } {
    return this.meta;
  }

  /**
   * Add a log entry for a system.
   * Server version tags source, adds area metadata, and queues for sync.
   */
  override addLogEntry(entry: DebugLogEntry): void {
    if (!this._enabled) return;

    const systemName = this.normalizeSystemName(entry.systemName);
    const info = this.ensureSystemDebugInfo(systemName);

    // Ensure entry has server source and area metadata
    entry.source = 'server';
    if (!entry.areaId && this.meta.areaId) {
      entry.areaId = this.meta.areaId;
    }
    if (!entry.areaType && this.meta.areaType) {
      entry.areaType = this.meta.areaType;
    }

    // Handle grouped logs
    if (entry.groupId) {
      if (!this.systemGroupLogEntries[systemName]) {
        this.systemGroupLogEntries[systemName] = {};
      }
      const existing = this.systemGroupLogEntries[systemName][entry.groupId];
      if (existing) {
        Object.assign(existing, entry);
        this.pendingSyncData.logs.push(entry);
        return;
      } else {
        this.systemGroupLogEntries[systemName][entry.groupId] = entry;
      }
    }

    info.logs.push(entry);
    if (info.logs.length > info.maxLogs) {
      info.logs.shift();
    }

    if (!this.logsBySystem[systemName]) {
      this.logsBySystem[systemName] = [];
    }
    this.logsBySystem[systemName].push(entry);
    if (this.logsBySystem[systemName].length > info.maxLogs) {
      this.logsBySystem[systemName].shift();
    }

    // Queue for sync
    this.pendingSyncData.logs.push(entry);
  }

  /**
   * Add a log entry for an entity.
   * Server version tags source and queues for sync.
   */
  override addEntityLogEntry(entry: DebugLogEntry): void {
    if (!this._enabled) return;

    const entityId = String(entry.entityId ?? entry.systemName.replace('ENTITY:', ''));
    const info = this.ensureEntityDebugInfo(entityId, entry.entityType);

    entry.source = 'server';

    if (entry.groupId) {
      if (!this.entityGroupLogEntries[entityId]) {
        this.entityGroupLogEntries[entityId] = {};
      }
      const existing = this.entityGroupLogEntries[entityId][entry.groupId];
      if (existing) {
        Object.assign(existing, entry);
        this.pendingSyncData.logs.push(entry);
        return;
      } else {
        this.entityGroupLogEntries[entityId][entry.groupId] = entry;
      }
    }

    info.logs.push(entry);
    if (info.logs.length > info.maxLogs) {
      info.logs.shift();
    }

    if (!this.logsByEntity[entityId]) {
      this.logsByEntity[entityId] = [];
    }
    this.logsByEntity[entityId].push(entry);
    if (this.logsByEntity[entityId].length > info.maxLogs) {
      this.logsByEntity[entityId].shift();
    }

    this.pendingSyncData.logs.push(entry);
  }

  /**
   * Update debug info for a system.
   * Server version delegates to base, then queues for sync if requested.
   */
  override updateSystemDebugInfo(
    systemName: string,
    info: Partial<Omit<SystemDebugInfo, 'name' | 'logs'>>,
    options?: SystemDebugUpdateOptions
  ): void {
    super.updateSystemDebugInfo(systemName, info, options);

    // Queue for sync if requested
    if (options?.sync) {
      this.pendingSyncData.systems.add(this.normalizeSystemName(systemName));
    }
  }

  /**
   * Update debug info for an entity.
   * Server version delegates to base, then queues for sync if requested.
   */
  override updateEntityDebugInfo(
    entityId: string | number,
    info: Partial<Omit<EntityDebugInfo, 'id' | 'logs'>>,
    options?: EntityDebugUpdateOptions
  ): void {
    super.updateEntityDebugInfo(entityId, info, options);

    if (options?.sync) {
      this.pendingSyncData.entities.add(entityId);
    }
  }

  /**
   * Clear all debug info.
   * Server version also resets pending sync data.
   */
  override clear(): void {
    super.clear();
    this.pendingSyncData = { systems: new Set(), entities: new Set(), logs: [] };
  }

  // -- Server-specific methods --

  /**
   * Send full debug state to a specific client.
   */
  private sendFullStateToClient(clientId: string): void {
    if (!this.dispatchToClient) return;

    this.dispatchToClient(clientId, {
      type: DEBUG_MESSAGES.FULL_DEBUG_STATE,
      to: this.name,
      data: {
        systems: this.systemDebugInfo,
        entities: this.entityDebugInfo,
        meta: this.meta,
      },
    });
  }

  /**
   * Flush pending sync data to all clients.
   */
  private flushSync(): void {
    if (!this._enabled) return;
    if (!this.dispatchToAllClients) return;

    // Sync logs
    if (this.pendingSyncData.logs.length > 0) {
      this.dispatchToAllClients({
        type: DEBUG_MESSAGES.LOG_ENTRY,
        to: this.name,
        data: { logs: this.pendingSyncData.logs },
      });
      this.pendingSyncData.logs = [];
    }

    // Sync system info
    if (this.pendingSyncData.systems.size > 0) {
      const systems: Record<string, SystemDebugInfo> = {};
      for (const name of this.pendingSyncData.systems) {
        const info = this.systemDebugInfo[name];
        if (info) {
          systems[name] = info;
        }
      }
      this.dispatchToAllClients({
        type: DEBUG_MESSAGES.SYSTEM_INFO_SYNC,
        to: this.name,
        data: { systems },
      });
      this.pendingSyncData.systems.clear();
    }

    // Sync entity info
    if (this.pendingSyncData.entities.size > 0) {
      const entities: Record<string, EntityDebugInfo> = {};
      for (const id of this.pendingSyncData.entities) {
        const info = this.entityDebugInfo[String(id)];
        if (info) {
          entities[String(id)] = info;
        }
      }
      this.dispatchToAllClients({
        type: DEBUG_MESSAGES.ENTITY_INFO_SYNC,
        to: this.name,
        data: { entities },
      });
      this.pendingSyncData.entities.clear();
    }
  }

  /**
   * Broadcast area info to clients.
   */
  broadcastAreaInfo(): void {
    if (!this._enabled || !this.dispatchToAllClients) return;

    this.dispatchToAllClients({
      type: DEBUG_MESSAGES.AREA_INFO,
      to: this.name,
      data: { meta: this.meta },
    });
  }
}
